import { BaseToast, Empty, Id, Loading, StylesContainer, isSuccess, useI18nForm } from "@dgs/core";
import {
	DndContext,
	DragEndEvent,
	DragOverlay,
	DragStartEvent,
	KeyboardSensor,
	MouseSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DataFieldOptionOrderIndexResource } from "../../../shared/types/dataFieldOption";
import { createDataFieldOptionService, dataFieldService } from "../shared/api/dataFieldService";
import { ListItem } from "../shared/ui/sortable_lists/ListItem";
import { SortableItem } from "./SortableItem";

const Wrapper = styled.div`
	display: grid;
	grid-auto-flow: row;
	align-content: start;
	width: 100%;
	gap: ${({ theme }) => theme.spacing(4)};
	margin: ${({ theme }) => theme.spacing(4)} 0;
`;

const SortableWrapper = styled.div`
	display: grid;
	gap: ${({ theme }) => theme.spacing(4)};
	align-content: start;
	grid-auto-rows: max-content;
	width: 100%;
`;

const Container = styled.div`
	width: 100%;
	display: flex;
	flex: 1;
`;

const Notice = styled(BaseToast)`
	width: 100%;
	max-width: unset;
`;

interface Props {
	dataFieldId: Id;
	onEditClick: (id: Id) => void;
	onDeleteClick: (id: Id) => void;
}

export const DataFieldOptionSortableList: FC<Props> = ({ dataFieldId, onEditClick, onDeleteClick }) => {
	const { currentLanguage: language } = useI18nForm();
	const dataFieldOptionService = createDataFieldOptionService(`${dataFieldId}`);
	const queryClient = useQueryClient();
	const { data, isLoading } = useQuery({
		queryKey: [...dataFieldOptionService.listKey, language],
		queryFn: () => dataFieldOptionService.listByLanguage(language).then((x) => x.data.data),
	});
	const [options, setOptions] = useState<DataFieldOptionOrderIndexResource[]>([]);
	const [activeItem, setActiveItem] = useState<DataFieldOptionOrderIndexResource | null>(null);
	const { t } = useTranslation();
	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);
	const canOrder = useMemo(() => {
		return data && !data.some((i) => i.isFallback);
	}, [data]);

	useEffect(() => {
		if (data) {
			setOptions(data);
		}
	}, [data]);

	const handleDragStart = useCallback(
		({ active }: DragStartEvent) => {
			setActiveItem(options.find((d) => d.id === active.id) || null);
		},
		[options],
	);

	const handleDragEnd = useCallback(
		async (event: DragEndEvent) => {
			const { active, over } = event;
			setActiveItem(null);

			if (over === null) {
				return;
			}

			if (active.id !== over.id) {
				const oldIndex = options.findIndex((d) => d.id === active.id);
				const newIndex = options.findIndex((d) => d.id === over.id);
				const newOptions = arrayMove(options, oldIndex, newIndex);

				setOptions(newOptions);

				const res = await dataFieldService.order(
					dataFieldId,
					language,
					newOptions.map((x) => x.id),
				);

				if (isSuccess(res)) {
					void queryClient.invalidateQueries({ queryKey: [...dataFieldOptionService.listKey, language] });
				}
			}
		},
		[dataFieldId, language, options, queryClient, dataFieldOptionService.listKey],
	);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
		>
			{!canOrder && (
				<Notice
					type="warning"
					title={t("Ordering disabled")}
					body={t(
						"There are one or more translations missing for the current language. Ordering is disabled until all translations have been set.",
					)}
					headerActions={null}
					footerActions={null}
				/>
			)}
			<Wrapper>
				<Container>
					<SortableWrapper>
						<SortableContext items={options} strategy={verticalListSortingStrategy}>
							{options.map((item) => (
								<SortableItem
									disabled={!canOrder}
									key={item.id}
									item={item}
									onEditClick={onEditClick}
									onDeleteClick={onDeleteClick}
								/>
							))}
							{options.length === 0 && <Empty>{t("No data field options available.")}</Empty>}
						</SortableContext>
					</SortableWrapper>
				</Container>
			</Wrapper>
			{createPortal(
				<StylesContainer>
					<DragOverlay>{activeItem ? <ListItem active>{activeItem.value}</ListItem> : null}</DragOverlay>
				</StylesContainer>,
				document.body,
			)}
		</DndContext>
	);
};
